import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'underscore';
import Loader from '../../components/Loader/Loader';
import Error from '../../components/Error/Error';
import * as cms from '../../shared/repos/cms/cms';
import cmsKeys from '../../shared/constants/cms';
import { getContentsByKey, getContentValueByKey, routeCountryPath } from '../../shared/utilities/common';
import * as elementsActions from '../../redux/actions/elements';
import OrderYourWaySection from './components/OrderYourWaySection';
import LimitedTimeSection from './components/LimitedTimeSection';
import ProductsHeader from '../../components/ProductsHeader/ProductsHeader';
import Carousel from './components/Carousel';
import amplitudeAnalytics from '../../shared/utilities/amplitude';
import { isBrandRefreshEnabled } from '../../shared/utilities/brandRefresh';
import { getDeliveryFaqCategoryPayload } from './helpers';
import FaqSection from './components/FaqSection';
import GiftingBrandRefreshFaqBlock from './components/GiftingBrandRefreshFaqBlock';
import {
  GIFTING_FAQ_CMS_CATEGORY_LABEL,
  GIFTING_FAQ_SECTION_HEADING,
} from './giftingFaqConstants';
import GiftingCategoryMenuBrandRefresh from './components/GiftingCategoryMenu/GiftingCategoryMenuBrandRefresh';
import { GIFTING_CATEGORY_SECTION_TITLE } from './components/GiftingCategoryMenu/GiftingCategoryMenuBrandRefresh.mocks';
import useGiftingCategoryMenu from './components/GiftingCategoryMenu/useGiftingCategoryMenu';
import GiftingSeasonalPromoBrandRefresh from './components/GiftingSeasonalPromoBrandRefresh/GiftingSeasonalPromoBrandRefresh';
import GiftingOccasionsBrandRefresh from './components/GiftingOccasionsBrandRefresh/GiftingOccasionsBrandRefresh';
import GiftingReviewsCorporateBrandRefresh from './components/GiftingReviewsCorporateBrandRefresh/GiftingReviewsCorporateBrandRefresh';
import GiftingHeroBrandRefresh from './components/GiftingHeroBrandRefresh/GiftingHeroBrandRefresh';

const exitIcon = require('../../components/Modal/imgs/exit-icon.svg');

function GiftingWaveDivider() {
  return (
    <div className="relative">
      <div className="overflow-hidden">
        <svg
          className="w-[200vw] md:w-full h-[56px] md:h-[60px] block"
          viewBox="0 0 1440 56"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="1440" height="56" fill="#FFFFFF" />
          <path
            d="M1440 22.1001C1410 22.1001 1380 27.7501 1350 39.0501C1290 61.6501 1230 61.6501 1170 39.0501C1140 27.7501 1110 22.1001 1080 22.1001C1050 22.1001 1020 27.7501 990 39.0501C930 61.6502 870 61.6502 810 39.0502C780 27.7501 750 22.1001 720 22.1001C690 22.1001 660 27.7502 630 39.0502C570 61.6502 510 61.6502 450 39.0502C420 27.7502 390 22.1002 360 22.1002C330 22.1002 300 27.7502 270 39.0502C210 61.6502 150 61.6502 90 39.0502C60 27.7502 30 22.1002 -2.96364e-06 22.1002L-4.89569e-06 0.000118259L1440 -7.62939e-06L1440 22.1001Z"
            fill="#461D58"
          />
        </svg>
      </div>
    </div>
  );
}

function GiftingCarouselBlock({ pageData }) {
  if (!pageData.carousel?.images?.length) {
    return null;
  }

  return (
    <div className="px-4 xl:px-0 w-full mx-auto mb-8">
      <Carousel images={pageData.carousel.images} cmsNode={pageData.carousel.cmsNode} />
    </div>
  );
}

GiftingCarouselBlock.propTypes = {
  pageData: PropTypes.shape({
    carousel: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.string),
      cmsNode: PropTypes.object,
    }),
  }).isRequired,
};

function GiftingLegacyLayout({ pageData, onButtonClick }) {
  return (
    <>
      <OrderYourWaySection
        orderYourWaySection={pageData.orderYourWaySection}
        onButtonClick={onButtonClick}
      />
      <GiftingWaveDivider />
      <div className="w-full relative bg-gradient-to-b from-white to-[#F6F3F7] flex flex-col item-center justify-center">
        <div className="px-4 xl:px-0 w-full max-w-screen-desktop mx-auto">
          <LimitedTimeSection
            limitedTimeSection={pageData.limitedTimeSection}
            onButtonClick={onButtonClick}
          />
        </div>
        <GiftingCarouselBlock pageData={pageData} />
      </div>
    </>
  );
}

GiftingLegacyLayout.propTypes = {
  pageData: PropTypes.shape({
    orderYourWaySection: PropTypes.object,
    limitedTimeSection: PropTypes.object,
    carousel: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.string),
      cmsNode: PropTypes.object,
    }),
  }).isRequired,
  onButtonClick: PropTypes.func.isRequired,
};

/**
 * Brand-refresh gifting landing page (`isBrandRefreshEnabled()`).
 * Section order matches design spec; mount new blocks here as they ship.
 */
function GiftingBrandRefreshLayout({ onCtaClick }) {
  const {
    categoryItems,
    activeCategoryId,
    categoryProducts,
    selectCategory,
    loading: giftingCategoryMenuLoading,
    menuError: giftingCategoryMenuError,
  } = useGiftingCategoryMenu();

  return (
    <>
      <GiftingHeroBrandRefresh onCtaClick={onCtaClick} />
      <GiftingCategoryMenuBrandRefresh
        sectionTitle={GIFTING_CATEGORY_SECTION_TITLE}
        items={categoryItems}
        activeId={activeCategoryId}
        products={categoryProducts}
        loading={giftingCategoryMenuLoading}
        menuError={giftingCategoryMenuError}
        onItemClick={selectCategory}
      />
      <GiftingSeasonalPromoBrandRefresh onCtaClick={onCtaClick} />
      <GiftingOccasionsBrandRefresh onCtaClick={onCtaClick} />
      <GiftingReviewsCorporateBrandRefresh onCtaClick={onCtaClick} />
      {/* TODO: logo ticker */}
    </>
  );
}

GiftingBrandRefreshLayout.propTypes = {
  onCtaClick: PropTypes.func.isRequired,
};

const Gifting = ({ setModalObject }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [faqSectionCategories, setFaqSectionCategories] = useState([]);
  const [expandedFaqId, setExpandedFaqId] = useState(null);
  const brandRefreshEnabled = isBrandRefreshEnabled();

  useEffect(() => {
    const updateContent = ({ loading: isLoading, content: newContent }) => {
      // Only update loading state if it's explicitly provided
      // This ensures loading stays true until the final response
      if (isLoading !== undefined) {
        setLoading(isLoading);
      }
      if (newContent !== undefined) {
        setContent(newContent);
      }
    };

    cms.getContent([cmsKeys.giftingPage], updateContent);
  }, []);

  useEffect(() => {
    if (!brandRefreshEnabled) {
      return undefined;
    }

    const updateFaqSections = ({ content: newContent }) => {
      if (newContent !== undefined) {
        setFaqSectionCategories(newContent);
      }
    };

    cms.getContent([cmsKeys.faqPage.faqSections], updateFaqSections);
    return undefined;
  }, [brandRefreshEnabled]);

  const giftingFaqSection = useMemo(() => {
    const { category: deliveryCategory, items: deliveryFaqItems } = getDeliveryFaqCategoryPayload(
      faqSectionCategories,
      GIFTING_FAQ_CMS_CATEGORY_LABEL,
    );

    if (!deliveryFaqItems.length) {
      return null;
    }

    return {
      title: GIFTING_FAQ_SECTION_HEADING,
      faqQuestions: deliveryFaqItems.map((item) => ({
        question: item.question,
        answer: item.answer,
        cmsKeyId: item.cmsKeyId,
        cmsNode: item.cmsNode,
        answerCmsNode: item.answerCmsNode,
      })),
      cmsNode: deliveryCategory,
    };
  }, [faqSectionCategories]);

  const showBrandRefreshFaqSection = brandRefreshEnabled
    && giftingFaqSection != null;

  const handleToggleFaq = useCallback((faqId) => {
    setExpandedFaqId((prevId) => (prevId === faqId ? null : faqId));
  }, []);

  const handleOpenImageModal = useCallback((imageUrl, altText = '') => {
    const handleClose = () => {
      setModalObject(null);
    };

    setModalObject({
      children: (
        <div className="relative w-full">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 w-6 h-6 cursor-pointer hover:opacity-70 transition-opacity bg-transparent border-none p-0 focus:outline-none focus:ring-2 focus:ring-dark focus:ring-offset-2"
            aria-label="Close modal"
          >
            <img
              alt="exit-icon"
              src={exitIcon}
              className="w-full h-full"
            />
          </button>
          <div className="p-5 flex justify-center items-center min-h-[400px]">
            <img
              src={imageUrl}
              alt={altText}
              className="max-w-full max-h-[85vh] object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = e.target.nextSibling;
                if (placeholder) {
                  placeholder.style.display = 'block';
                }
              }}
            />
            <div className="hidden p-15 text-center font-montserratRegular text-dark">
              <p className="text-xl font-bold mb-2">Image</p>
              <p className="text-sm text-darkElevationPrimary">
                Image not found. Please add the image URL to CMS.
              </p>
            </div>
          </div>
        </div>
      ),
      exit: true,
      maxWidth: '90vw'
    });
  }, [setModalObject]);


  const extractUrl = (markdown) => {
    const match = markdown.match(/\((.*?)\)/); // extract content inside parentheses
    return match ? match[1] : "";
  };

  const handleButtonClick = useCallback((link, buttonType, buttonText = '', contentSection = 'Gifting LP') => {
    if (!link) return;

    const btn = buttonText != null ? String(buttonText).trim() : '';
    const href = link != null ? String(link).trim() : '';
    const contentElement = btn || href || 'Gifting CTA';
    amplitudeAnalytics.trackCmsMappedPageClick({
      pathname: history.location?.pathname
        || (typeof window !== 'undefined' ? window.location.pathname : ''),
      contentSection,
      contentElement,
    });

    if (href.startsWith('#') && typeof document !== 'undefined') {
      const targetId = href.slice(1);
      const targetEl =
        document.getElementById(targetId)
        || document.querySelector(`[data-region="${targetId}"]`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    // Handle based on button type
    if (buttonType === 'image_modal_link') {
      // Open image in modal with button text as alt tag
      handleOpenImageModal(link, buttonText);
      return;
    }

    if (buttonType === 'external_link') {
      // Open external link in new window/tab
      if (link.startsWith('http')) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = link;
      }
      return;
    }

    if (buttonType === 'internal_link') {
      // Navigate internally
      if (link.startsWith('http')) {
        // If it's a full URL but marked as internal, extract pathname
        try {
          const url = new URL(link);
          history.push(routeCountryPath(url.pathname));
        } catch (e) {
          // If URL parsing fails, treat as relative path
          history.push(routeCountryPath(link));
        }
      } else {
        history.push(routeCountryPath(link));
      }
      return;
    }

    // Fallback: if no button type is set, check if it's an external link
    if (link.startsWith('http')) {
      window.location.href = link;
      return;
    }
    // Default to internal navigation
    history.push(routeCountryPath(link));
  }, [history, handleOpenImageModal]);

  const normalizeApiResponse = useCallback((contentData) => {
    const normalizedData = {
      heroSection: {},
      orderYourWaySection: { orderCards: [] },
      limitedTimeSection: { limitedTimeCards: [] },
      carousel: { carousel: [] },
      bgImages: {}
    };

    // If no API content, return empty structure
    if (!contentData || contentData.length === 0) {
      return normalizedData;
    }

    // Get children from the first array element (gifting_page)
    const giftingPage = contentData[0];
    const pageChildren = giftingPage?.children || [];

    // Build normalized data structure from API response only

    // Normalize Hero Section
    const heroSection = _.find(pageChildren, { key: 'hero_section' });
    if (heroSection && heroSection.children) {
      const giftingConciergeButton = _.find(heroSection.children, { key: 'gifting_concierge_button' });
      const selfServeButton = _.find(heroSection.children, { key: 'self_serve_button' });

      normalizedData.heroSection = {
        ...normalizedData.heroSection,
        title: getContentValueByKey(heroSection.children, 'title'),
        description: getContentValueByKey(heroSection.children, 'description'),
        image: getContentValueByKey(heroSection.children, 'image'),
        giftBoxImage: getContentValueByKey(heroSection.children, 'gift_box_image'),
        giftingConciergeLink: giftingConciergeButton ? getContentValueByKey(giftingConciergeButton.children || [], 'link') : null,
        giftingConciergeButtonText: giftingConciergeButton ? getContentValueByKey(giftingConciergeButton.children || [], 'text') : null,
        giftingConciergeButtonType: giftingConciergeButton ? getContentValueByKey(giftingConciergeButton.children || [], 'button_type') : null,
        giftingConciergeButtonNode: giftingConciergeButton,
        selfServeLink: selfServeButton ? getContentValueByKey(selfServeButton.children || [], 'link') : null,
        selfServeButtonText: selfServeButton ? getContentValueByKey(selfServeButton.children || [], 'text') : null,
        selfServeButtonType: selfServeButton ? getContentValueByKey(selfServeButton.children || [], 'button_type') : null,
        selfServeButtonNode: selfServeButton,
        cmsNode: heroSection
      };
    }

    // Normalize Order Your Way Section
    const orderYourWaySection = _.find(pageChildren, { key: 'order_your_way_section' });
    if (orderYourWaySection) {
      normalizedData.orderYourWaySection = {
        ...normalizedData.orderYourWaySection,
        title: getContentValueByKey(orderYourWaySection.children || [], 'title'),
        cmsNode: orderYourWaySection
      };

      const orderCards = getContentsByKey(orderYourWaySection.children || [], 'order_cards');
      if (orderCards.length > 0) {
        normalizedData.orderYourWaySection.orderCards = orderCards.map(card => {
          const buttonNode = _.find(card.children || [], { key: 'button' });
          return {
            title: getContentValueByKey(card.children || [], 'title'),
            description: getContentValueByKey(card.children || [], 'description'),
            image: getContentValueByKey(card.children || [], 'image'),
            buttonText: buttonNode ? getContentValueByKey(buttonNode.children || [], 'text') : null,
            buttonLink: buttonNode ? getContentValueByKey(buttonNode.children || [], 'link') : null,
            buttonType: buttonNode ? getContentValueByKey(buttonNode.children || [], 'button_type') : null,
            buttonNode,
            cmsKeyId: card.cmsKeyId,
            cmsNode: card
          };
        });
      }
    }

    // Normalize Limited Time Section
    const limitedTimeSection = _.find(pageChildren, { key: 'limited_time_section' });
    if (limitedTimeSection) {
      normalizedData.limitedTimeSection = {
        ...normalizedData.limitedTimeSection,
        title: getContentValueByKey(limitedTimeSection.children || [], 'title'),
        dateText: getContentValueByKey(limitedTimeSection.children || [], 'date_text'),
        cmsNode: limitedTimeSection
      };

      const limitedTimeCards = getContentsByKey(limitedTimeSection.children || [], 'limited_time_cards');
      if (limitedTimeCards.length > 0) {
        normalizedData.limitedTimeSection.limitedTimeCards = limitedTimeCards.map(card => 
        {
          const buttonNode = _.find(card.children || [], { key: 'button' });
          return {
          title: getContentValueByKey(card.children || [], 'title'),
          description: getContentValueByKey(card.children || [], 'description'),
          image: getContentValueByKey(card.children || [], 'image'),
          backgroundColor: getContentValueByKey(card.children || [], 'background_color'),
          buttonLabel: buttonNode ? getContentValueByKey(buttonNode.children || [], 'label') : null,
          buttonLink: buttonNode ? getContentValueByKey(buttonNode.children || [], 'link') : null,
          buttonType: buttonNode ? getContentValueByKey(buttonNode.children || [], 'button_type') : null,
          buttonNode,
          cmsKeyId: card.cmsKeyId,
          cmsNode: card
        }
      }
      );
      }
    }
    // Normalize Background Images
    const bgImages = _.find(pageChildren, { key: 'bg_images' });
    if (bgImages && bgImages.children) {
      normalizedData.bgImages = {
        heroSectionRightImage: getContentValueByKey(bgImages.children, 'hero_section_right_image'),
        orderYourWaySectionLeftImage: getContentValueByKey(bgImages.children, 'order_your_way_section_left_image'),
        limitedTimeSectionRightImage: getContentValueByKey(bgImages.children, 'limited_time_section_right_image'),
        cmsNode: bgImages
      };
    }

    // Normalize Carousel
    const carousel = _.find(pageChildren, { key: "carousel" });
    if (carousel) {
      const imageUrls = carousel.children
        .filter((child) => child.key === "image")
        .map((imgNode) => {
          const urlNode = imgNode.children.find((n) => n.key === "url");
          return extractUrl(urlNode?.value || "");
        });
    
      normalizedData.carousel = {
        ...normalizedData.carousel,
        images: imageUrls,   // <-- final array of image URLs
        cmsNode: carousel,
      };
    }

    return normalizedData;
  }, []);
  

  // Memoize the normalized page data to avoid recalculating on every render
  const pageData = useMemo(() => {
    return normalizeApiResponse(content);
  }, [content, normalizeApiResponse]);

  if (loading) {
    return (
      <div className="w-full flex justify-center bg-dark min-h-screen">
        <div className="w-full">
          <Loader id="loader" />
        </div>
      </div>
    );
  }

  // Display 404 error if API response is empty
  if (!content || content.length === 0) {
    return <Error errorType="404" />;
  }

  const brandRefresh = isBrandRefreshEnabled();

  return (
    <div className="w-full min-h-screen bg-dark">
      <div className="w-full">
        <ProductsHeader floatMenuHide="true" hideHeroBanner={brandRefresh} />
        {brandRefresh ? (
          <GiftingBrandRefreshLayout onCtaClick={handleButtonClick} />
        ) : (
          <GiftingLegacyLayout
            pageData={pageData}
            onButtonClick={handleButtonClick}
          />
        )}
      </div>
      {showBrandRefreshFaqSection && (
        <GiftingBrandRefreshFaqBlock>
          <FaqSection
            faqSection={giftingFaqSection}
            expandedQuestionId={expandedFaqId}
            onToggleFaq={handleToggleFaq}
          />
        </GiftingBrandRefreshFaqBlock>
      )}
    </div>
  );
};

Gifting.propTypes = {
  setModalObject: PropTypes.func.isRequired,
};

export const mapDispatchToProps = dispatch => ({
  setModalObject: value => dispatch(elementsActions.setModalObject(value)),
});

export default connect(null, mapDispatchToProps)(Gifting);
